import { useNavigate, useParams } from "react-router-dom";

import MainContainer from "../../layout/MainContainer";

import { Button, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import {
  useAddPaymentModeMutation,
  useLazyPaymentModeDetailQuery,
  useUpdatePaymentModeMutation,
} from "../../service/paymentModes";
import { errorToast, successToast } from "../../helpers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import { isNumberPrice, isString, isValidInput } from "../../utils/validations";
import { formatNumberWithCommas } from "../../utils/commoFunctions";
import Loader from "../../constants/Loader";

const AddPaymentMode = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [getPaymentModeById] = useLazyPaymentModeDetailQuery();
  const [addPaymentMode] = useAddPaymentModeMutation();
  const [updatePaymentMode] = useUpdatePaymentModeMutation();

  const getDetails = async (id: number) => {
    setLoading(true);

    try {
      const response = await getPaymentModeById({
        id,
      }).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        formik.setFieldValue("mode_name", response?.data?.payment_mode_name);
        formik.setFieldValue(
          "total_payment",
          formatNumberWithCommas(
            response?.data?.payment_total?.toString() || "0"
          )
        );
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      mode_name: "",
      total_payment: "",
    },

    validationSchema: Yup.object({
      mode_name: Yup.string()
        .required("Mode name is required")
        .min(2, "Minimum 2 characters are required"),
      total_payment: Yup.string()
        .required("This field is required")
        .min(2, "Minimum 2 digits are required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        payment_mode_name: values.mode_name,
        payment_total: Number(values.total_payment?.replace(/,/g, "")),
      };

      try {
        setLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;
        if (id) {
          response = await updatePaymentMode({
            body: encryptedBody,
            id: Number(id),
          }).unwrap();
        } else {
          response = await addPaymentMode(encryptedBody).unwrap();
        }

        if (response?.status === 200) {
          setLoading(false);
          if (id) {
            successToast("Payment mode updated successfully");
          } else {
            successToast("Payment mode added successfully");
          }
          navigate("/manage-payments/payment-modes");
        } else {
          setLoading(false);
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      getDetails(Number(id));
    }
  }, [id]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? "Edit" : "Add"} Payment Mode</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/manage-payments/payment-modes")}>
              Manage Payments
            </span>{" "}
            - {id ? "Edit" : "Add"} Payment Mode
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_blue" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Mode Name</label>
              <TextField
                variant="outlined"
                placeholder="Mode Name"
                fullWidth
                hiddenLabel
                className="text_field"
                name="mode_name"
                id="mode_name"
                inputProps={{ maxLength: 40 }}
                value={formik.values.mode_name}
                onChange={(val) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.mode_name && formik.errors.mode_name}
              />
            </div>
            <div className="control_group w_50">
              <label>Total Amount</label>
              <TextField
                variant="outlined"
                placeholder="Total Amount"
                fullWidth
                hiddenLabel
                className="text_field"
                name="total_payment"
                id="total_payment"
                inputProps={{ maxLength: 11 }}
                value={formik.values.total_payment}
                onChange={(val) => {
                  const { value } = val.target;

                  // Remove commas for proper validation and length counting
                  let numericValue = value.replace(/,/g, "");

                  // Prevent starting with 0 unless it's just "0"
                  if (numericValue.length && numericValue.startsWith("0")) {
                    numericValue = numericValue.replace(/^0+/, ""); // Remove leading zeros
                  }

                  // Validate number format and length limit
                  if (isNumberPrice(numericValue) && numericValue.length <= 9) {
                    const formattedValue = formatNumberWithCommas(numericValue);
                    formik.setFieldValue("total_payment", formattedValue);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.total_payment && formik.errors.total_payment
                }
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddPaymentMode;
